import { useMemo, useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import {
  useAdminReports,
  useAdminAbusePatterns,
  buildAdminReportExportUrl,
  useCompanies,
} from '@/lib/adminApi';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function currentMonthYYYYMM(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function ReportsPage() {
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [month, setMonth] = useState<string>(currentMonthYYYYMM());
  const [billable, setBillable] = useState<boolean | undefined>(undefined);

  const filter = useMemo(() => ({ companyId, month, billable }), [companyId, month, billable]);

  const { data, isLoading, isError } = useAdminReports(filter);
  const { data: abuseData } = useAdminAbusePatterns({ companyId, month });
  const { data: companiesData } = useCompanies();

  function triggerExport(format: 'pdf' | 'csv') {
    const url = buildAdminReportExportUrl({ companyId, month, format });
    window.location.href = url;
  }

  function onBillableChange(val: string) {
    if (val === 'all') setBillable(undefined);
    else if (val === 'yes') setBillable(true);
    else setBillable(false);
  }

  const abusePatterns = abuseData?.patterns ?? [];
  const showCompanyColumn = !companyId;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      <h1 className="text-2xl font-semibold">Reports</h1>

      {/* A. Filters bar */}
      <div className="bg-background rounded-md border p-4 sticky top-16 z-10 flex flex-wrap items-end gap-4">
        <div className="flex flex-col">
          <label
            htmlFor="admin-reports-company"
            className="text-xs uppercase text-muted-foreground mb-1 font-medium"
          >
            Company
          </label>
          <select
            id="admin-reports-company"
            aria-label="Company"
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            value={companyId ?? ''}
            onChange={(e) => setCompanyId(e.target.value || undefined)}
          >
            <option value="">All companies</option>
            {(companiesData?.companies ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="admin-reports-month"
            className="text-xs uppercase text-muted-foreground mb-1 font-medium"
          >
            Month
          </label>
          <input
            id="admin-reports-month"
            type="month"
            aria-label="Month"
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-xs uppercase text-muted-foreground mb-1 font-medium">Billable</span>
          <Select
            value={billable === undefined ? 'all' : billable ? 'yes' : 'no'}
            onValueChange={onBillableChange}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Billable only</SelectItem>
              <SelectItem value="no">Non-billable only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" disabled={isLoading} onClick={() => triggerExport('csv')}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" disabled={isLoading} onClick={() => triggerExport('pdf')}>
            <Download className="h-4 w-4 mr-1" />
            Export PDF
          </Button>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load report data — try again.</AlertDescription>
        </Alert>
      )}

      {/* B. Usage Summary tiles */}
      <section aria-labelledby="usage-summary-heading">
        <h2 id="usage-summary-heading" className="text-base font-semibold mb-4">
          Usage Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="py-6">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-semibold tabular-nums">
                    {data?.summary.totalBillableIncrements ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Total billable increments
                  </div>
                  <div className="text-xs text-muted-foreground">this month</div>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-6">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-semibold tabular-nums">
                    {data?.summary.totalNonBillableSessions ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Total non-billable sessions
                  </div>
                  <div className="text-xs text-muted-foreground">this month</div>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-6">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div
                    className="text-2xl font-semibold tabular-nums"
                    style={{
                      color:
                        (data?.summary.totalOverageIncrements ?? 0) > 0 ? '#DC2626' : undefined,
                    }}
                  >
                    {data?.summary.totalOverageIncrements ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Overage increments pushed to GHL
                  </div>
                  <div className="text-xs text-muted-foreground">this month</div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* C. Session Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Session Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32" />
          ) : (data?.breakdown.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No sessions in this period.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {showCompanyColumn && <TableHead>Company</TableHead>}
                  <TableHead>Ticket</TableHead>
                  <TableHead>Duration (min)</TableHead>
                  <TableHead>Increments</TableHead>
                  <TableHead>Billable</TableHead>
                  <TableHead>Adjusted</TableHead>
                  <TableHead>Waived</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.breakdown ?? []).map((row) => (
                  <TableRow
                    key={row.sessionId}
                    className={row.isWaived ? 'opacity-60' : ''}
                    style={row.isAdjusted ? { borderLeft: '2px solid #D97706' } : undefined}
                  >
                    {showCompanyColumn && <TableCell>{row.companyName}</TableCell>}
                    <TableCell>{row.ticketTitle ?? '—'}</TableCell>
                    <TableCell className={row.isWaived ? 'line-through' : ''}>
                      {row.durationMinutes}
                    </TableCell>
                    <TableCell>{row.incrementsBilled}</TableCell>
                    <TableCell>{row.isBillable ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.isAdjusted ? 'Yes' : '—'}</TableCell>
                    <TableCell>{row.isWaived ? 'Yes' : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* D. Abuse Pattern Alerts — only renders if patterns detected */}
      {abusePatterns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" style={{ color: '#D97706' }} />
              Usage Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {abusePatterns.map((p, i) => (
                <div
                  key={`${p.companyId}-${p.patternType}-${i}`}
                  className="border-l-4 pl-4 py-2"
                  style={{ borderColor: '#D97706' }}
                >
                  <div className="font-semibold text-base">{p.companyName}</div>
                  <div
                    className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ background: '#FEF3C7', color: '#92400E' }}
                  >
                    {p.patternType === 'high-adjustment-frequency'
                      ? 'High adjustment frequency'
                      : 'Unusual session length'}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
